import { useCallback, useEffect, useState } from 'react';
import { useLogto } from '@logto/react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logout, setBridgeAuthenticated, setStatus, setUser } from '@/store/auth.slice';
import {
  clearStoredTokens,
  consumeAuthStartParam,
  getRedirectUri,
  hasStoredAccessToken,
  isTelegramWebView,
  setAccessTokenGetter,
  startTelegramSignIn,
} from '@/lib/logto';
import { useGetMeQuery } from '@/services/endpoints/auth';

/**
 * Drives sign-in in two contexts:
 *
 *  - **Standalone web** (browser tab, Telegram dev mock): standard Logto SDK
 *    redirect flow. `useHandleSignInCallback` on `/auth/callback` finishes it.
 *  - **Real Telegram WebView**: Logto's interactive flow can't run inside the
 *    embedded WebView (Google blocks OAuth there). Instead `startTelegramSignIn`
 *    opens the system browser via `WebApp.openLink`. The bridge in
 *    `<AuthCallback>` deep-links back via `t.me/.../app?startapp=auth_<code>`,
 *    and on relaunch `consumeAuthStartParam` exchanges the code for tokens.
 *
 * Auth state lives in Redux (`state.auth.bridgeAuthenticated`) so all
 * components that read `useLogtoAuth()` stay in sync — particularly important
 * for sign-out, where the trigger lives in <Profile>/<Settings> but the gate
 * lives in <AppShell>.
 *
 * Resume handling: when Telegram brings the existing Mini App back to
 * foreground via the t.me deep link (instead of fully re-launching it), no
 * component re-mounts, so a plain mount-time effect would miss the new
 * `auth_<code>` startParam. We re-run the consume on `visibilitychange` /
 * `focus` events and dedup by storing the last consumed code in localStorage.
 */
const LAST_CONSUMED_CODE = 'qlash.last_consumed_auth_code';

export function useLogtoAuth() {
  const dispatch = useAppDispatch();
  const inTelegram = isTelegramWebView();

  const { isAuthenticated: sdkAuthenticated, isLoading, getAccessToken, signIn, signOut } =
    useLogto();

  const bridgeAuthenticated = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const [bridgeChecked, setBridgeChecked] = useState(!inTelegram);

  useEffect(() => {
    if (!inTelegram) return;

    let cancelled = false;
    let running = false;

    const tryConsume = async (reason: string): Promise<void> => {
      if (running) return;
      if (hasStoredAccessToken()) {
        dispatch(setBridgeAuthenticated(true));
        setBridgeChecked(true);
        return;
      }
      running = true;
      try {
        // Peek startParam first to dedup repeat invocations on the same code.
        let startParam: string | undefined;
        try {
          const { retrieveLaunchParams } = await import('@tma.js/sdk-react');
          startParam = retrieveLaunchParams().tgWebAppStartParam;
        } catch {
          /* ignore */
        }
        const code = startParam?.startsWith('auth_') ? startParam.slice(5) : null;
        if (code && localStorage.getItem(LAST_CONSUMED_CODE) === code) {
          console.info('[auth] skipping already-consumed code (', reason, ')');
          setBridgeChecked(true);
          return;
        }
        console.info('[auth] attempting consume (', reason, ') startParam=', startParam?.slice(0, 24));
        const result = await consumeAuthStartParam();
        if (cancelled) return;
        if (result === true) {
          if (code) localStorage.setItem(LAST_CONSUMED_CODE, code);
          dispatch(setBridgeAuthenticated(true));
        }
        setBridgeChecked(true);
      } finally {
        running = false;
      }
    };

    void tryConsume('mount');

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void tryConsume('visibilitychange');
    };
    const onFocus = (): void => void tryConsume('focus');
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [inTelegram, dispatch]);

  // Expose getAccessToken to non-React callers (RTK Query). The wrapper in
  // `lib/logto.ts` prefers our manually-stored token, so this getter is only
  // used in the standalone web fallback.
  useEffect(() => {
    setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  const isAuthenticated = bridgeAuthenticated || sdkAuthenticated;
  const effectiveLoading = inTelegram ? !bridgeChecked && !bridgeAuthenticated : isLoading;

  // Reflect loading state in Redux so common components can show spinners.
  useEffect(() => {
    dispatch(setStatus(effectiveLoading ? 'loading' : 'idle'));
  }, [effectiveLoading, dispatch]);

  // Pull the QLASH user record once authenticated.
  const { data: me } = useGetMeQuery(undefined, { skip: !isAuthenticated });
  useEffect(() => {
    if (me) dispatch(setUser(me));
  }, [me, dispatch]);

  const triggerSignIn = useCallback(() => {
    if (inTelegram) void startTelegramSignIn();
    else void signIn(getRedirectUri());
  }, [inTelegram, signIn]);

  const triggerSignOut = useCallback(() => {
    clearStoredTokens();
    dispatch(logout());
    // Drop Logto SDK in-memory state too. Calling `signOut()` without a
    // post-logout URI just clears tokens — no redirect — so it's safe inside
    // the Telegram WebView. We pass an empty arg to avoid `window.location`
    // navigation while still resetting the SDK's React state.
    void signOut().catch(() => {
      /* SDK may throw if it had no session — ignore. */
    });
  }, [signOut, dispatch]);

  return {
    isAuthenticated,
    isLoading: effectiveLoading,
    signIn: triggerSignIn,
    signOut: triggerSignOut,
  };
}
